<template>
  <o-dialog
    ref="modal"
    :title="$t('components.product.actions.removeInventory.title')"
    :info="$t('components.product.actions.removeInventory.info')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('components.product.actions.removeInventory.title'), type: 'confirm', callback: handleConfirm },
    ]"
    :valid="validate"
  >
    <div v-if="product" class="mt-4 text-left ">
      <o-form ref="form" class="h-full">
        <div class="flex space-x-4 mb-6">
          <o-field-group v-slot="{ status }" rules="required|min_value:1" class="w-full" :label="$t('general.amount')" :info="$t('components.product.actions.removeInventory.amountInfo')">
            <number-field v-model="adjustment.adjustment" :status="status" />
          </o-field-group>
        </div>

        <div class="flex space-x-4 mb-6">
          <o-field-group v-slot="{ status }" rules="required" class="w-full" :label="$t('general.reason')" :info="$t('components.product.actions.removeInventory.reasonInfo')">
            <select-field v-model="adjustment.type" :options="adjustmentTypeOptions" :status="status" />
          </o-field-group>
        </div>

        <div v-if="isAdjustmentTypeOther" class="flex space-x-4 mb-6">
          <o-field-group v-slot="{ status }" rules="required" class="w-full" :label="$t('general.other')" :info="$t('components.product.actions.otherInfo')">
            <text-field v-model="adjustment.otherLabel" :status="status" />
          </o-field-group>
        </div>
      </o-form>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import {
  OFieldGroup,
  OForm,
} from '@/components/elements'

import {
  SelectField,
  NumberField,
  TextField,
} from '@/components/fields'

import { ProductInventoryTransaction, TransactionType, TransactionTypeLabels } from '~/_api/models/productInventoryTransaction'

interface Props {
  product: any
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
  (e: 'update:product', product: any): void
  (e: 'error'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const { $api, $i18n, $notification } = useNuxtApp()

const modal = ref<any>()
const form = ref<any>()
const adjustment = ref({
  adjustment: 0,
  type: '',
  otherLabel: '',
})

const adjustmentTypeOptions = computed(() => {
  return ProductInventoryTransaction.removeTypes()
    .map((type) => ({
      label: $i18n.t(TransactionTypeLabels[type]),
      value: type,
    }))
})

const isAdjustmentTypeOther = computed(() => {
  return adjustment.value.type === TransactionType.other
})

const open = (product?: any) => {
  modal.value.open()
}

const validate = () => {
  return form.value.validate()
}

const handleConfirm = async () => {
  emit('update:loading', true)

  try {
    const adjustmentData = {
      ...adjustment.value,
      adjustment: adjustment.value.adjustment * -1,
    }
    const response = await $api.product.adjustInventory(
      String(props.product.idProduct),
      adjustmentData
    )
    const product = response.data

    if (product) {
      $notification.toast({
        title: $i18n.t('components.product.actions.removeInventory.success'),
        text: $i18n.t('components.product.actions.removeInventory.successInfo', { title: props.product.title }),
        variant: 'success',
      })

      emit('success')
      emit('update:product', product)

      return true
    }
  } catch (e) {
    $notification.toast({
      title: $i18n.t('components.product.actions.removeInventory.error'),
      text: $i18n.t('components.product.actions.removeInventory.errorInfo', { title: props.product.title }),
    })

    emit('error')
  }
}

defineExpose({ open })
</script>
